/**
 * Category Color Utilities
 * Provides theme-aware color management for category badges and UI elements
 */

/**
 * Improved color palette with better visibility in both light and dark modes
 * Optimized to 10 colors for clean single-line display
 * These colors have been chosen for:
 * - Good contrast in both themes
 * - Distinctiveness from each other
 * - Accessibility (WCAG AA compliance)
 */
export const CATEGORY_COLORS = [
  '#E53935', // Red - vibrant but not too bright
  '#F57C00', // Orange - stands out
  '#FB8C00', // Amber - warm
  '#7CB342', // Light Green - balanced
  '#00897B', // Teal - good contrast
  '#039BE5', // Blue - readable
  '#8E24AA', // Purple - our brand color variant
  '#D81B60', // Pink/Magenta - distinct
  '#6c3082', // Brand Purple
  '#468230', // Brand Green
];

/**
 * Adjusts a hex color's brightness for better visibility in light mode
 * @param {string} hexColor - Hex color code (e.g., '#FF6B6B')
 * @param {boolean} isDarkMode - Current theme mode
 * @param {number} adjustmentFactor - How much to darken/lighten (0-1, default 0.15)
 * @returns {string} Adjusted hex color
 */
export function getThemeAwareColor(hexColor, isDarkMode, adjustmentFactor = 0.15) {
  // In dark mode, return original color
  if (isDarkMode) {
    return hexColor;
  }

  // In light mode, darken the color for better visibility
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Darken by reducing each channel
  const darken = (channel) => Math.max(0, Math.floor(channel * (1 - adjustmentFactor)));

  const newR = darken(r);
  const newG = darken(g);
  const newB = darken(b);

  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
}

/**
 * Gets background color with appropriate opacity for category badges
 * @param {string} color - Base color
 * @param {boolean} isDarkMode - Current theme mode
 * @param {boolean} isSelected - Whether the item is selected
 * @returns {string} CSS color with opacity
 */
export function getCategoryBackgroundColor(color, isDarkMode, isSelected = false) {
  const themeColor = getThemeAwareColor(color, isDarkMode, 0.1);
  
  if (isSelected) {
    return `${themeColor}40`; // 25% opacity for selected
  }
  
  return `${themeColor}20`; // 12.5% opacity for normal
}

/**
 * Gets text color that contrasts well with the background
 * @param {string} backgroundColor - Background hex color
 * @param {boolean} isDarkMode - Current theme mode
 * @returns {string} '#ffffff' for dark backgrounds, dark color for light backgrounds
 */
export function getContrastTextColor(backgroundColor, isDarkMode) {
  // Remove # and convert to RGB
  const hex = backgroundColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return white for dark colors, dark for light colors
  return luminance > 0.5 ? '#1a1a1a' : '#ffffff';
}

/**
 * Gets border color with appropriate contrast for the theme
 * @param {string} color - Base color
 * @param {boolean} isDarkMode - Current theme mode
 * @returns {string} Border color
 */
export function getCategoryBorderColor(color, isDarkMode) {
  // In light mode, use darker version of color for better visibility
  // In dark mode, use slightly lighter version
  return getThemeAwareColor(color, isDarkMode, isDarkMode ? -0.1 : 0.2);
}

/**
 * Complete category color styling object for consistent use
 * @param {string} color - Base category color
 * @param {boolean} isDarkMode - Current theme mode
 * @param {boolean} isSelected - Whether the item is selected
 * @returns {Object} Complete styling object with backgroundColor, color, border, etc.
 */
export function getCategoryStyles(color, isDarkMode, isSelected = false) {
  const themeColor = getThemeAwareColor(color, isDarkMode, 0.1);
  const backgroundColor = getCategoryBackgroundColor(color, isDarkMode, isSelected);
  const borderColor = getCategoryBorderColor(color, isDarkMode);
  
  return {
    backgroundColor,
    color: themeColor,
    borderColor,
    '--category-color': themeColor,
    '--category-bg': backgroundColor,
    '--category-border': borderColor,
  };
}

/**
 * Validates and returns a category color from the palette
 * @param {number} index - Index in the color palette
 * @returns {string} Hex color code
 */
export function getCategoryColorByIndex(index) {
  return CATEGORY_COLORS[index % CATEGORY_COLORS.length];
}

/**
 * Gets CSS color with alpha channel (rgba)
 * @param {string} hexColor - Hex color code
 * @param {number} alpha - Alpha value (0-1)
 * @returns {string} RGBA color string
 */
export function hexToRgba(hexColor, alpha = 1) {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

